import { array, bool, func, number, object, oneOf, string } from 'prop-types'
import {
  CheckBox,
  kuiperClassNames,
  Conditional,
  ListManager,
  Loader,
} from '@opallabs/kuiper'
import { track } from '@opallabs/quanta'
import { isInWorkflowPopover } from '../helpers/matchers'
import { findFirstNonCompletePhase } from '../helpers/phaseHelpers'
import { AddStageButton } from '../Workflow/StageList/AddStageButton'
import CompletedPhases from './CompletedPhases'
import { getLoaderTheme, isFinalApprovalReached } from './helpers/items'
import { shouldShowStartWorkflow } from './helpers/stateHelpers'
import PhaseContainer from './PhaseContainer'
import phaseListContext from './phaseListContext'
import WorkflowStarter from './WorkflowStarter'

import './styles.scss'

// Wrapper to pass onDarkBackground since PhaseList's outer container is dark
const AddStageButtonOnDark = (props) => (
  <AddStageButton {...props} onDarkBackground />
)

const onToggleWorkflowLockAndTrack = (onToggleWorkflowLock, e) => {
  if (e) {
    track('Configure: Lock Phases')
  }
  return onToggleWorkflowLock(e)
}

const UnmanagedPhaseList = ({
  completedPhases,
  contentAccountId,
  createPhase,
  currentUser,
  enableListManagerEditing,
  entitySaveMethod,
  getWorkflow,
  isLimitedRole,
  isTemplate,
  isWorkflowLockable,
  loaded,
  locked,
  onChangeDate,
  onChangeName,
  onChangePhaseOptions,
  onDestroyPhase,
  onDestroyPhaseItem,
  onItemMoved,
  onRetractApprovals,
  onSendForApproval,
  onStartWorkflow,
  onToggleWorkflowLock,
  phases,
  theme = 'light',
  updatePhase,
  workflowParentType,
  workflowStarted,
  workflowValid,
  ...otherProps
}) => {
  const finalApprovalReached = isFinalApprovalReached(phases)
  const { workflowLocation } = otherProps
  const showStartWorkflowButton = shouldShowStartWorkflow(
    workflowLocation,
    workflowStarted,
    isTemplate,
    phases.length
  )
  const allPhasesComplete = !findFirstNonCompletePhase(phases)

  return (
    <div
      className={kuiperClassNames(
        'phaseList',
        `${workflowParentType}Workflow`,
        'hasBgColor',
        `theme-${theme}`,
        { workflowStarted }
      )}
    >
      <phaseListContext.Provider value={{ getWorkflow }}>
        <Conditional condition={showStartWorkflowButton}>
          <WorkflowStarter
            contentAccountId={contentAccountId}
            entitySaveMethod={entitySaveMethod}
            isLimitedRole={isLimitedRole}
            onStartWorkflow={onStartWorkflow}
            {...{
              theme,
            }}
            workflowLocation={workflowLocation}
            workflowValid={workflowValid}
            {...otherProps}
          />
        </Conditional>

        <Conditional condition={loaded && completedPhases.length}>
          <CompletedPhases
            allPhasesComplete={allPhasesComplete}
            finalApprovalReached={finalApprovalReached}
            phases={completedPhases}
            {...{
              theme,
            }}
            {...otherProps}
          />
        </Conditional>

        <Conditional condition={loaded}>
          <ListManager
            {...otherProps}
            addItemButton={AddStageButtonOnDark}
            animate={true}
            animationName={kuiperClassNames('insert-phase')}
            createItem={createPhase}
            currentUser={currentUser}
            editing={enableListManagerEditing}
            entitySaveMethod={entitySaveMethod}
            finalApprovalReached={finalApprovalReached}
            isLimitedRole={isLimitedRole}
            isWorkflowTemplate={isTemplate}
            itemRenderer={PhaseContainer}
            items={phases}
            onChangeDate={onChangeDate}
            onChangeName={onChangeName}
            onChangePhaseOptions={onChangePhaseOptions}
            onDestroyPhaseItem={onDestroyPhaseItem}
            onItemMoved={onItemMoved}
            onRetractApprovals={onRetractApprovals}
            onSendForApproval={onSendForApproval}
            placeholder="whenEmpty"
            removeItem={onDestroyPhase}
            skipCompletedPhases={true}
            theme={theme}
            updateItem={updatePhase}
            workflowStarted={workflowStarted}
          />
        </Conditional>

        <Conditional condition={!isInWorkflowPopover(workflowLocation)}>
          <Loader
            loaded={loaded}
            minHeight="201px"
            theme={getLoaderTheme({ isTemplate, theme, workflowLocation })}
          />
        </Conditional>

        <Conditional condition={isTemplate && isWorkflowLockable}>
          <CheckBox
            label="Do not allow these phases to be modified on content"
            onChange={onToggleWorkflowLockAndTrack.bind(
              this,
              onToggleWorkflowLock
            )}
            theme={theme}
            value={locked}
          />
        </Conditional>
      </phaseListContext.Provider>
    </div>
  )
}

UnmanagedPhaseList.propTypes = {
  completedPhases: array.isRequired,
  contentAccountId: number,
  createPhase: func,
  currentPhase: object,
  currentUser: object,
  enableListManagerEditing: bool,
  entitySaveMethod: func,
  getWorkflow: func,
  isLimitedRole: bool,
  isTemplate: bool,
  isWorkflowLockable: bool,
  loaded: bool,
  locked: bool,
  onAddPhase: func,
  onChangeDate: func,
  onChangeName: func,
  onChangePhaseOptions: func,
  onDestroyPhase: func,
  onDestroyPhaseItem: func,
  onItemMoved: func,
  onMovePhaseDown: func,
  onMovePhaseUp: func,
  onRetractApprovals: func,
  onSendForApproval: func,
  onSetPhase: func,
  onStartWorkflow: func.isRequired,
  onToggleWorkflowLock: func,
  phases: array.isRequired,
  theme: oneOf(['dark', 'light']),
  updatePhase: func,
  workflowLocation: string,
  workflowParentType: string,
  workflowStarted: bool,
  workflowValid: bool,
}

export default UnmanagedPhaseList
